import React, { useState } from 'react';
import { motion } from 'framer-motion';
import { useNavigate } from 'react-router-dom';
import axios from 'axios';
import SettingsGroup, { SettingsBlock } from '../common/editorial/SettingsGroup';
import { FieldLabel, EditorialSelect } from '../common/editorial/EditorialControls';

const CREDENTIALS = ['CFP', 'CFA', 'RIA', 'ChFC', 'EA', 'CPA'];
const SPECIALTIES = [
  'Retirement Planning',
  'Tax Strategy',
  'Investment Management',
  'Debt Reduction',
  'Estate Planning',
  'Insurance & Protection',
  'Small Business',
  'General Financial Planning',
];

const AdvisorApplicationForm = () => {
  const navigate = useNavigate();
  const [form, setForm] = useState({
    license_type: '',
    license_number: '',
    years_experience: '',
    bio: '',
    booking_url: '',
    hourly_rate: '',
    specialties: [],
  });
  const [loading, setLoading] = useState(false);
  const [success, setSuccess] = useState(false);
  const [error, setError] = useState('');

  const toggleSpecialty = (s) => {
    setForm((f) => ({
      ...f,
      specialties: f.specialties.includes(s) ? f.specialties.filter((x) => x !== s) : [...f.specialties, s],
    }));
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    if (
      !form.license_type ||
      !form.license_number ||
      form.specialties.length === 0 ||
      !form.bio ||
      form.bio.length < 50
    ) {
      setError(
        'Please fill in all required fields and select at least one specialty. Bio must be at least 50 characters.'
      );
      return;
    }
    setLoading(true);
    setError('');
    try {
      await axios.post('/api/community/advisors/apply', {
        ...form,
        years_experience: parseInt(form.years_experience, 10) || 0,
        hourly_rate: form.hourly_rate ? parseFloat(form.hourly_rate) : null,
      });
      setSuccess(true);
    } catch (e) {
      const detail = e.response?.data?.detail;
      const message =
        typeof detail === 'string'
          ? detail
          : Array.isArray(detail)
            ? detail.map((d) => d.msg || d.message || JSON.stringify(d)).join('. ')
            : e.response?.status === 401
              ? 'Please log in to submit an application.'
              : e.response?.status === 403
                ? 'You do not have permission to submit an application.'
                : 'Submission failed. Please try again.';
      setError(message);
    } finally {
      setLoading(false);
    }
  };

  if (success) {
    return (
      <div className="min-h-screen bg-bone flex items-center justify-center p-6">
        <motion.div
          initial={{ opacity: 0, y: 6 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ duration: 0.4, ease: 'easeOut' }}
          className="aura-card max-w-md text-center p-8"
        >
          <h2 className="font-display text-h2 text-primary mb-3">Application submitted</h2>
          <p className="text-body text-secondary mb-4">
            We will review your credentials and respond within 48 hours.
          </p>
          <p className="text-label text-faint mb-6">
            You will appear in the advisor directory once your application is approved.
          </p>
          <button type="button" onClick={() => navigate('/advisors')} className="btn-primary">
            View advisor directory
          </button>
        </motion.div>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-bone p-6 md:p-8">
      <div className="max-w-2xl mx-auto">
        <button
          type="button"
          onClick={() => navigate('/advisors')}
          className="text-secondary hover:text-primary mb-6 block transition-colors duration-300"
        >
          ← Back to advisors
        </button>

        <motion.div
          initial={{ opacity: 0, y: 6 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ duration: 0.4, ease: 'easeOut' }}
        >
          <header className="mb-8 pb-6 border-b border-hairline">
            <h1 className="font-display text-h1 text-primary mb-2">Apply as a verified advisor</h1>
            <p className="text-body text-secondary">Join our network of vetted financial professionals.</p>
          </header>

          <form onSubmit={handleSubmit} className="space-y-10">
            <SettingsGroup title="Credentials">
              <SettingsBlock>
                <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
                  <div>
                    <FieldLabel htmlFor="license-type">Credential</FieldLabel>
                    <EditorialSelect
                      id="license-type"
                      value={form.license_type}
                      onChange={(e) => setForm({ ...form, license_type: e.target.value })}
                    >
                      <option value="">Select…</option>
                      {CREDENTIALS.map((c) => (
                        <option key={c} value={c}>
                          {c}
                        </option>
                      ))}
                    </EditorialSelect>
                  </div>
                  <div>
                    <FieldLabel htmlFor="license-number">License number</FieldLabel>
                    <input
                      id="license-number"
                      value={form.license_number}
                      onChange={(e) => setForm({ ...form, license_number: e.target.value })}
                      placeholder="e.g. CFP-12345"
                      className="input-editorial"
                    />
                  </div>
                </div>
              </SettingsBlock>
            </SettingsGroup>

            <SettingsGroup title="Experience & rate">
              <SettingsBlock>
                <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
                  <div>
                    <FieldLabel htmlFor="years-exp">Years experience</FieldLabel>
                    <input
                      id="years-exp"
                      type="number"
                      min="0"
                      value={form.years_experience}
                      onChange={(e) => setForm({ ...form, years_experience: e.target.value })}
                      className="input-editorial figure"
                    />
                  </div>
                  <div>
                    <FieldLabel htmlFor="hourly-rate">Session rate ($/hr)</FieldLabel>
                    <input
                      id="hourly-rate"
                      type="number"
                      min="0"
                      value={form.hourly_rate}
                      onChange={(e) => setForm({ ...form, hourly_rate: e.target.value })}
                      placeholder="e.g. 150"
                      className="input-editorial figure"
                    />
                  </div>
                </div>
              </SettingsBlock>
            </SettingsGroup>

            <SettingsGroup title="Specialties" description="Select all that apply">
              <SettingsBlock>
                <div className="flex flex-wrap gap-2">
                  {SPECIALTIES.map((s) => (
                    <button
                      type="button"
                      key={s}
                      onClick={() => toggleSpecialty(s)}
                      className={`px-3 py-1.5 rounded-md text-label border transition-colors duration-300 ${
                        form.specialties.includes(s)
                          ? 'bg-pine-tint border-pine text-pine'
                          : 'border-hairline text-secondary hover:bg-pine-tint/50'
                      }`}
                    >
                      {s}
                    </button>
                  ))}
                </div>
              </SettingsBlock>
            </SettingsGroup>

            <SettingsGroup title="Bio & booking">
              <SettingsBlock className="space-y-4">
                <div>
                  <FieldLabel htmlFor="bio">Bio (min 50 characters)</FieldLabel>
                  <textarea
                    id="bio"
                    value={form.bio}
                    onChange={(e) => setForm({ ...form, bio: e.target.value })}
                    rows={4}
                    placeholder="Tell potential clients about your background and approach…"
                    className="input-editorial resize-none"
                  />
                  <p className="text-label text-faint mt-1">{form.bio.length}/1000 characters</p>
                </div>
                <div>
                  <FieldLabel htmlFor="booking-url">Booking URL (Calendly, etc.)</FieldLabel>
                  <input
                    id="booking-url"
                    value={form.booking_url}
                    onChange={(e) => setForm({ ...form, booking_url: e.target.value })}
                    placeholder="https://calendly.com/your-name"
                    className="input-editorial"
                  />
                </div>
              </SettingsBlock>
            </SettingsGroup>

            {error && (
              <div className="surface-panel p-3 border border-danger/40">
                <p className="text-body text-danger">{error}</p>
              </div>
            )}

            <div className="flex flex-wrap gap-3">
              <button type="submit" disabled={loading} className="btn-primary flex-1 min-w-[200px]">
                {loading ? 'Submitting…' : 'Submit application'}
              </button>
              <button type="button" onClick={() => navigate('/advisors')} className="btn-ghost">
                Cancel
              </button>
            </div>
          </form>
        </motion.div>
      </div>
    </div>
  );
};

export default AdvisorApplicationForm;
